/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * "Sinds je bezoek van {date}" — what changed in the comparison since the
 * visitor was last here (docs/plan-vergelijker-resultaten-2026-09-30.md
 * §12.4 B3), under the trust strip and above the basis line.
 *
 * It says only what the snapshot proves (plan §12.2; the rules as code are
 * src/lib/compareChanges.ts):
 *
 * - the date is the visitor's OWN previous visit, never "gewijzigd op";
 * - numbers only — the ERE price (a reading of the dated series, so its "toen"
 *   has a real peildatum), commission, service costs, payout frequency —
 *   never CMS text;
 * - a provider with no row in the snapshot is "nieuw in de vergelijking", one
 *   with no row now "niet meer in de vergelijking": a withdrawal and a
 *   `showInComparison` toggle look the same, so nothing stronger is said.
 *
 * The lines, in this order: the price, the per-offer changes, the new names,
 * the gone names. The per-offer changes are limited to the offers in the
 * current view (what the visitor filtered away is not news to them), and equal
 * lines of one provider are merged: payout is a provider setting, so two offers
 * of one provider would otherwise say the same thing twice. Five lines show;
 * "+ {n} meer" shows the rest and hands the focus to the first of them.
 * "Verbergen" hides the strip for the rest of the tab and hands the focus on.
 *
 * Client-only by construction: the engine fills its state from storage in an
 * effect, so the strip is never in the bake or the first client render. Static
 * once shown — no motion, no `initial`.
 */

import React, { useEffect, useId, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useLanguage, type Language } from '../../contexts/LanguageContext';
import type { ChangeField, CompareDiff } from '../../lib/compareChanges';
import {
  eurDecimals,
  formatCurrencyEUR,
  formatDateShort,
  formatErePrice,
  formatInteger,
  formatNumber,
  toLocaleTag,
} from '../../lib/formatters';
import { nextFocusable } from '../../lib/nextFocusable';
import { fillTemplate } from '../../lib/templateVars.js';

/** One line of the strip, before it has words. */
export type ChangeLine =
  | { kind: 'price'; then: number; now: number }
  | {
      kind: 'offer';
      field: ChangeField;
      name: string;
      /** null only for a model change: the side without a model is a flat offer. */
      then: number | string | null;
      now: number | string | null;
      /** The offers (row ids) this line speaks for — more than one when equal lines merged. */
      ids: string[];
    }
  | { kind: 'new'; names: string[] }
  | { kind: 'gone'; names: string[] };

/** Lines shown before "+ {n} meer". */
export const VISIBLE_CHANGE_LINES = 5;

/**
 * The strip's lines for the offers in view (`viewIds`: the row ids of the
 * current filtered list), in the strip's order. Per-offer changes outside the
 * view are left out; equal lines (one provider, one field, the same then and
 * now) become one. New and gone names are about the comparison as a whole.
 */
export function changeLines(diff: CompareDiff, viewIds: ReadonlySet<string>): ChangeLine[] {
  const lines: ChangeLine[] = [];
  if (diff.price) lines.push({ kind: 'price', then: diff.price.then, now: diff.price.now });
  const merged = new Map<string, Extract<ChangeLine, { kind: 'offer' }>>();
  for (const change of diff.changed) {
    if (!viewIds.has(change.id)) continue;
    // An unknown side says nothing — except for the model, whose null side is a flat offer.
    if (change.field !== 'model' && (change.then === null || change.now === null)) continue;
    const key = JSON.stringify([change.name, change.field, change.then, change.now]);
    const line = merged.get(key);
    if (line) {
      line.ids.push(change.id);
      continue;
    }
    const fresh: Extract<ChangeLine, { kind: 'offer' }> = {
      kind: 'offer',
      field: change.field,
      name: change.name,
      then: change.then,
      now: change.now,
      ids: [change.id],
    };
    merged.set(key, fresh);
    lines.push(fresh);
  }
  if (diff.added.length > 0) lines.push({ kind: 'new', names: [...diff.added] });
  if (diff.gone.length > 0) lines.push({ kind: 'gone', names: [...diff.gone] });
  return lines;
}

/** The offers the lines name a change of: `provider_count` on compare_changes_view. */
export const changedOfferCount = (lines: readonly ChangeLine[]): number =>
  new Set(lines.flatMap((line) => (line.kind === 'offer' ? line.ids : []))).size;

const OFFER_LINE_KEYS: Record<ChangeField, string> = {
  commission: 'comparison.changes_commission',
  service: 'comparison.changes_service',
  payout: 'comparison.changes_payout',
  // A changed pricing model is named, never printed: its values are JSON.
  model: 'comparison.changes_model',
};

const NBSP = String.fromCharCode(0xa0);

/**
 * A commission as the row prints it (ResultRow.tsx, `formatCommission`): the
 * page's own number format, a whole rate without decimals ("15", never "15,0")
 * and at most two, so a rate is never rounded into another one.
 */
const formatRate = (fee: number, language: Language): string =>
  formatNumber(fee, language, { maximumFractionDigits: 2 });

/**
 * One value of a per-offer line, in the words the row uses for it: a
 * commission as the row prints it, service costs per year (or "geen"), a
 * payout frequency by its label. A word inside the sentence starts lower case.
 */
function offerValue(
  field: ChangeField,
  value: number | string | null,
  language: Language,
  t: (key: string) => string
): string {
  const lower = (text: string) => text.toLocaleLowerCase(toLocaleTag(language));
  if (field === 'model' || value === null) return '';
  if (field === 'payout') return lower(t(`table.payout_values.${String(value)}`));
  if (typeof value !== 'number') return String(value);
  if (field === 'commission') return `${formatRate(value, language)}%`;
  if (value === 0) return lower(t('table.none'));
  return `${formatCurrencyEUR(value, language, eurDecimals(value))}${t('table.per_year_short')}`;
}

/** A line in words. */
export function changeLineText(
  line: ChangeLine,
  language: Language,
  t: (key: string) => string
): string {
  switch (line.kind) {
    case 'price':
      return fillTemplate(t('comparison.changes_price'), {
        now: formatErePrice(line.now, language),
        then: formatErePrice(line.then, language),
      });
    case 'offer':
      return fillTemplate(t(OFFER_LINE_KEYS[line.field]), {
        name: line.name,
        then: offerValue(line.field, line.then, language, t),
        now: offerValue(line.field, line.now, language, t),
      });
    case 'new':
      return fillTemplate(t('comparison.changes_new'), { names: line.names.join(', ') });
    case 'gone':
      return fillTemplate(t('comparison.changes_gone'), { names: line.names.join(', ') });
  }
}

const lineKey = (line: ChangeLine): string =>
  line.kind === 'offer' ? `offer-${line.field}-${line.ids.join('+')}` : line.kind;

export interface ChangesStripProps {
  /** The visitor's previous visit (epoch ms): the strip's date. */
  since: number;
  /** `changeLines(...)` for the current view; the strip renders nothing without one. */
  lines: ChangeLine[];
  /** "Verbergen": the engine reports it and remembers it for the tab. */
  onDismiss: () => void;
}

export const ChangesStrip: React.FC<ChangesStripProps> = ({ since, lines, onDismiss }) => {
  const { t, language } = useLanguage();
  const headingId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const firstRevealedRef = useRef<HTMLLIElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [focusRevealed, setFocusRevealed] = useState(false);

  // "+ {n} meer" disappears with the click: the focus goes on to the first
  // line it revealed, never back to the page.
  useEffect(() => {
    if (!focusRevealed) return;
    setFocusRevealed(false);
    firstRevealedRef.current?.focus();
  }, [focusRevealed]);

  if (lines.length === 0) return null;

  const hidden = expanded ? 0 : Math.max(0, lines.length - VISIBLE_CHANGE_LINES);
  const shown = hidden > 0 ? lines.slice(0, VISIBLE_CHANGE_LINES) : lines;
  const date = formatDateShort(new Date(since), language).replace(/ /g, NBSP);

  const dismiss = () => {
    // The button that has the focus is about to go: hand it on to the next control.
    const root = rootRef.current;
    if (root && root.contains(document.activeElement)) nextFocusable(root)?.focus();
    onDismiss();
  };

  return (
    // From lg a card of reading width, not the full row: the lines are short,
    // and "Verbergen" stays beside the heading it hides.
    <div
      ref={rootRef}
      data-changes-strip
      className="mt-3 lg:max-w-3xl rounded-xl border border-slate-700/50 bg-slate-800/60 px-3.5 py-2.5"
    >
      <div className="flex items-center justify-between gap-3">
        <h3 id={headingId} className="text-xs font-semibold text-fg-muted">
          {fillTemplate(t('comparison.changes_title'), { date })}
        </h3>
        {/* 44 px to tap below lg, 36 from it; the negative margins keep the
          header line as tall as its text. */}
        <button
          type="button"
          onClick={dismiss}
          aria-describedby={headingId}
          className="-my-2.5 -mr-2 inline-flex min-h-11 lg:min-h-9 shrink-0 items-center gap-1 rounded-lg px-2 text-xs font-semibold text-slate-300 hover:bg-slate-700/50 hover:text-slate-100 focus-ring transition-colors"
        >
          <X className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          {t('comparison.changes_dismiss')}
        </button>
      </div>
      <ul className="mt-1.5 space-y-1 text-sm leading-snug text-slate-200 text-pretty">
        {shown.map((line, index) => (
          <li
            key={lineKey(line)}
            ref={index === VISIBLE_CHANGE_LINES ? firstRevealedRef : undefined}
            tabIndex={index === VISIBLE_CHANGE_LINES ? -1 : undefined}
            className="rounded-sm focus-ring"
          >
            {changeLineText(line, language, t)}
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => {
            setExpanded(true);
            setFocusRevealed(true);
          }}
          className="-mb-2 mt-0.5 inline-flex min-h-11 lg:min-h-9 items-center rounded-sm text-xs font-bold text-emerald-300 [html.light_&]:text-[#065f46] underline underline-offset-2 hover:text-emerald-200 focus-ring"
        >
          {fillTemplate(t('comparison.changes_more'), { n: formatInteger(hidden, language) })}
        </button>
      )}
    </div>
  );
};
